import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSettings } from "@/contexts/SettingsContext";
import { CalendarCheck, X, Sparkles } from "lucide-react";

/**
 * Sticky "Reserve" banner that slides down once the user scrolls past the hero.
 * After ~6s it auto-collapses to a small floating pill. Dismissible by the user.
 * Hidden on mobile to avoid duplicating the fixed bottom action bar.
 */
export default function ReserveSticky() {
  const { t } = useTranslation();
  const { settings: s } = useSettings();
  const location = useLocation();

  const [visible, setVisible] = useState(false);
  const [compact, setCompact] = useState(false);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem("le17_sticky_dismissed") === "1");

  // Hide on reservation page itself and admin
  const hideOnRoute = location.pathname.startsWith("/reservation") || location.pathname.startsWith("/admin");

  useEffect(() => {
    if (dismissed || hideOnRoute) return;
    const onScroll = () => {
      if (window.scrollY > 800 && !visible) setVisible(true);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [visible, dismissed, hideOnRoute]);

  // Auto-collapse after 6 seconds
  useEffect(() => {
    if (!visible) return;
    const id = setTimeout(() => setCompact(true), 6000);
    return () => clearTimeout(id);
  }, [visible]);

  const dismiss = () => {
    setDismissed(true);
    sessionStorage.setItem("le17_sticky_dismissed", "1");
  };

  if (dismissed || !visible || hideOnRoute) return null;

  return (
    <>
      {/* Full banner */}
      {!compact && (
        <div
          data-testid="reserve-sticky-banner"
          className="hidden lg:block fixed top-0 left-0 right-0 z-50 animate-slideDown"
          style={{ animation: "slideDown 0.55s cubic-bezier(.2,.7,.2,1) both" }}
        >
          <div className="bg-[#050505]/95 backdrop-blur-md border-b border-[#D4AF37]/40">
            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-3 flex items-center gap-5">
              <Sparkles size={16} className="text-[#D4AF37] shrink-0" />
              <div className="flex-1 text-sm text-[#F8F5EF]/90">
                <span className="overline mr-3 text-[#D4AF37]">{t("home.reservation_overline")}</span>
                <span>{t("hero.subtitle")}</span>
              </div>
              <Link to="/reservation" data-testid="sticky-reserve-cta" className="btn-gold text-[10px] py-2.5 px-5 whitespace-nowrap">
                <CalendarCheck size={14} /> {t("nav.reserve")}
              </Link>
              {s?.whatsapp_raw && (
                <a
                  data-testid="sticky-whatsapp-cta"
                  href={`https://wa.me/${s.whatsapp_raw}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[10px] tracking-[0.22em] uppercase text-[#F8F5EF]/80 hover:text-[#D4AF37] whitespace-nowrap"
                >
                  WhatsApp →
                </a>
              )}
              <button
                onClick={dismiss}
                aria-label="Close"
                data-testid="sticky-close"
                className="text-[#F8F5EF]/50 hover:text-[#D4AF37] transition-colors ml-2"
              >
                <X size={18} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Compact pill (after collapse) */}
      {compact && (
        <Link
          to="/reservation"
          data-testid="reserve-sticky-pill"
          className="hidden lg:inline-flex fixed top-24 right-6 z-50 items-center gap-2 bg-[#D4AF37] text-[#050505] px-5 py-3 text-[10px] tracking-[0.22em] uppercase font-medium shadow-2xl hover:bg-[#E5C869] transition-all"
          style={{ animation: "slideInRight 0.5s cubic-bezier(.2,.7,.2,1) both" }}
        >
          <CalendarCheck size={14} /> {t("nav.reserve")}
        </Link>
      )}

      <style>{`
        @keyframes slideDown {
          from { transform: translateY(-100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes slideInRight {
          from { transform: translateX(120%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </>
  );
}
